import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Search,
  ChevronDown,
  Ticket,
  MapPin,
  Accessibility,
  Sparkles,
  HelpCircle,
  MessageCircle,
  Mail,
  Bell,
  Send,
  Calendar,
  Hotel,
  Users,
  Clock,
  Shield,
  Heart,
  Info
} from 'lucide-react';
import Navigation from '../components/Navigation';

const FAQsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openAccordion, setOpenAccordion] = useState(null);

  const categories = ['All', 'Tickets', 'Venue', 'Accessibility', 'Experience', 'General'];

  const faqs = [
    // TICKETS
    {
      category: 'Tickets',
      question: 'How do I buy tickets for KNOWN CON?',
      answer: 'Tickets are available on our website through the Buy Tickets button. Choose your pass type (single day, multi-day, or VIP) and complete checkout. You\'ll receive a confirmation email with your digital ticket.'
    },
    {
      category: 'Tickets',
      question: 'Can I transfer or refund my ticket?',
      answer: 'Tickets are non-refundable but can be transferred to another person up to 48 hours before the event. Contact our support team with both parties\' information to process the transfer.'
    },
    {
      category: 'Tickets',
      question: 'How do I activate my badge?',
      answer: 'Visit the Badge Activation desk at the main entrance with your digital ticket and photo ID. Our team will print your official badge with your name and access level. Learn more in our Attendee Guide.'
    },
    {
      category: 'Tickets',
      question: 'What\'s the difference between ticket types?',
      answer: 'Single-day passes grant access to one day of events. Multi-day passes cover all three days. VIP passes include priority seating, exclusive workshops, and meet-and-greet opportunities with speakers.'
    },

    // VENUE
    {
      category: 'Venue',
      question: 'Where is KNOWN CON being held?',
      answer: 'KNOWN CON takes place at the Los Angeles Convention Center, 1201 S Figueroa Street, Los Angeles, CA 90015. The venue is fully accessible and offers multiple entry points.'
    },
    {
      category: 'Venue',
      question: 'What are the daily event hours?',
      answer: 'Doors open at 8:00 AM for badge pickup. Main sessions run from 9:00 AM to 9:00 PM each day. Evening worship and fellowship events extend until 10:30 PM. Check the full schedule for detailed timing.'
    },
    {
      category: 'Venue',
      question: 'Is parking available at the venue?',
      answer: 'Yes, on-site parking is $20/day at the South Hall entrance. Nearby lots offer rates from $15/day. ADA-compliant spaces are available. Visit our Hotel & Travel page for more transportation options.'
    },
    {
      category: 'Venue',
      question: 'Are there food options inside the venue?',
      answer: 'Multiple food vendors and cafes are located throughout the convention center offering breakfast, lunch, and dinner options. Dietary accommodations include vegetarian, vegan, and gluten-free choices.'
    },

    // ACCESSIBILITY
    {
      category: 'Accessibility',
      question: 'How do I request ADA assistance?',
      answer: 'Complete our online ADA Request Form or visit the Accessibility Desk at the main entrance. Our team provides wheelchair assistance, sign language interpretation, reserved seating, and quiet spaces. Learn more on our ADA Assistance page.'
    },
    {
      category: 'Accessibility',
      question: 'Is there reserved seating for guests with disabilities?',
      answer: 'Yes, reserved seating areas are available in all main venues. Show your ADA badge at any session entrance, and our volunteers will guide you to designated seating with companion space.'
    },
    {
      category: 'Accessibility',
      question: 'Are service animals allowed?',
      answer: 'Service animals are welcome in all public areas. Water and relief stations are marked on venue maps. Emotional support animals require pre-approval through our ADA request form.'
    },

    // EXPERIENCE
    {
      category: 'Experience',
      question: 'What activities or sessions can I attend?',
      answer: 'KNOWN CON features keynote speakers, worship sessions, interactive workshops, prayer gatherings, vendor marketplace, small group discussions, and evening concerts. All pass holders can access main stage events.'
    },
    {
      category: 'Experience',
      question: 'How do I see the event schedule?',
      answer: 'Visit our Event Schedule page for the full three-day timeline. Download the mobile app for real-time updates, session reminders, and personalized schedule building.'
    },
    {
      category: 'Experience',
      question: 'Can I meet the speakers?',
      answer: 'VIP pass holders have exclusive meet-and-greet access. All attendees can visit the speaker Q&A lounge during designated times. Book signings occur after main stage sessions.'
    },
    {
      category: 'Experience',
      question: 'Is there a quiet space for prayer or reflection?',
      answer: 'Yes, we provide dedicated Quiet Rooms on each floor for prayer, meditation, and sensory breaks. These spaces are open all day and clearly marked on venue maps.'
    },

    // GENERAL
    {
      category: 'General',
      question: 'Is this a faith-based event?',
      answer: 'Yes, KNOWN CON is a Christian faith-based conference celebrating belief, purpose, and community. All backgrounds and denominations are welcome with open arms.'
    },
    {
      category: 'General',
      question: 'What should I bring with me?',
      answer: 'Bring your digital ticket, photo ID, comfortable shoes, notebook, business cards, phone charger, and reusable water bottle. Dress casually — most attendees wear jeans and comfortable attire.'
    },
    {
      category: 'General',
      question: 'Are children allowed?',
      answer: 'Children under 12 attend free with a registered adult. We offer Kids Ministry programming during main sessions. Nursing rooms and family restrooms are available throughout the venue.'
    },
    {
      category: 'General',
      question: 'How can I volunteer or become a sponsor?',
      answer: 'Visit our Get Involved page to explore volunteer opportunities and sponsorship packages. Volunteers receive free admission, a conference t-shirt, and behind-the-scenes access.'
    },
    {
      category: 'General',
      question: 'Can I purchase merchandise at the event?',
      answer: 'Yes! Our official merch store features t-shirts, hoodies, journals, art prints, and worship music. Online pre-orders receive a 15% discount and priority pickup.'
    },
    {
      category: 'General',
      question: 'What is your COVID-19 policy?',
      answer: 'We follow local health guidelines. Masks are optional but welcome. Hand sanitizer stations are throughout the venue. Please stay home if you\'re experiencing symptoms.'
    }
  ];

  const topQuestions = [
    {
      icon: Ticket,
      title: 'Ticket Prices',
      answer: 'Single-day: $49 | Multi-day: $120 | VIP: $299'
    },
    {
      icon: MapPin,
      title: 'Event Location',
      answer: 'LA Convention Center, Los Angeles, CA'
    },
    {
      icon: Clock,
      title: 'Event Hours',
      answer: '9:00 AM - 9:00 PM daily (Aug 15-17)'
    },
    {
      icon: Users,
      title: 'Who Can Attend',
      answer: 'Open to all ages, faiths welcome'
    },
    {
      icon: Hotel,
      title: 'Parking Info',
      answer: 'On-site $20/day, nearby lots from $15'
    }
  ];

  const helpOptions = [
    {
      icon: MessageCircle,
      title: 'Chat with Us',
      description: 'Get instant answers from our support team',
      link: '#chat'
    },
    {
      icon: Mail,
      title: 'Email the Team',
      description: 'Send us your questions and we\'ll respond within 24 hours',
      link: '#email'
    },
    {
      icon: Bell,
      title: 'Check Latest Updates',
      description: 'Subscribe to our newsletter for announcements',
      link: '#newsletter'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-[10%] text-white text-8xl">?</div>
          <div className="absolute top-40 right-[15%] text-white text-6xl">?</div>
          <div className="absolute bottom-20 left-[20%] text-white text-7xl">?</div>
          <div className="absolute bottom-32 right-[25%] text-white text-9xl">?</div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm mb-6 text-gray-300">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-gray-300">Conference Info</span>
            <span>/</span>
            <span className="text-white font-medium">FAQs</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="text-[#CFA64B]">FAQs</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Got questions? We've got answers to help you plan your KNOWN CON journey.
          </p>
        </div>
      </div>

      {/* Quick Access Buttons */}
      <div className="bg-[#0B1B3B] py-4">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/attendee-guide" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              Attendee Guide
            </a>
            <span className="text-gray-500">|</span>
            <a href="/hotel-travel" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              Hotel & Travel
            </a>
            <span className="text-gray-500">|</span>
            <a href="/ada-assistance" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              ADA Assistance
            </a>
            <span className="text-gray-500">|</span>
            <a href="#contact" className="text-white hover:text-[#CFA64B] transition-colors text-sm font-medium">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Search + Category Filters */}
      <div className="sticky top-[80px] z-40 bg-white border-b border-gray-200 py-6 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for a topic (tickets, travel, venue...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:border-[#CFA64B] focus:outline-none text-[#0B1B3B] text-lg"
            />
          </div>

          {/* Category Chips */}
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'bg-[#CFA64B] text-[#0B1B3B]'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Accordion */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          {Object.keys(groupedFaqs).length === 0 ? (
            <div className="text-center py-20">
              <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">No results found</h3>
              <p className="text-gray-600">Try a different search term or category</p>
            </div>
          ) : (
            Object.entries(groupedFaqs).map(([category, questions]) => (
              <div key={category} className="mb-12">
                <h2 className="text-2xl font-bold text-[#0B1B3B] mb-6 uppercase tracking-wide">
                  {category}
                </h2>
                <div className="space-y-4">
                  {questions.map((faq, idx) => {
                    const accordionKey = `${category}-${idx}`;
                    const isOpen = openAccordion === accordionKey;
                    
                    return (
                      <Card 
                        key={accordionKey}
                        className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all duration-200"
                      >
                        <CardContent className="p-0">
                          <button
                            onClick={() => setOpenAccordion(isOpen ? null : accordionKey)}
                            className="w-full flex items-center justify-between p-6 text-left"
                          >
                            <span className="text-lg font-bold text-[#0B1B3B] pr-4">
                              {faq.question}
                            </span>
                            <ChevronDown 
                              className={`w-6 h-6 text-[#CFA64B] flex-shrink-0 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-6 pt-0">
                              <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Top 5 Questions */}
      <div className="py-20" style={{ backgroundColor: '#F6F8FB' }}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Top 5 Questions</h2>
            <p className="text-xl text-gray-600">Quick answers to what everyone's asking</p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {topQuestions.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={idx}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                      <Icon className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0B1B3B] mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Still Need Help */}
      <div className="py-20 bg-gradient-to-b from-[#FFF9E9] to-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Still Need Help?</h2>
            <p className="text-xl text-gray-600">Our support team and volunteers are here to assist</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {helpOptions.map((option, idx) => {
              const Icon = option.icon;
              return (
                <a
                  key={idx}
                  href={option.link}
                  className="block group"
                >
                  <Card className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 h-full">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                        <Icon className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B1B3B] mb-3 group-hover:text-[#CFA64B] transition-colors">
                        {option.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{option.description}</p>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feedback CTA */}
      <div className="py-20 bg-gradient-to-b from-white to-[#F6F8FB]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <div className="mb-8">
            <Send className="w-16 h-16 text-[#CFA64B] mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-6">Help Us Improve</h2>
          <p className="text-xl text-gray-700 mb-10 leading-relaxed">
            Didn't find what you were looking for? Let us know so we can make your experience even better.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            <Heart className="w-5 h-5 mr-2" />
            Submit Feedback
          </Button>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Ready to experience KNOWN CON?
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Join thousands of believers for three days of faith, purpose, and community
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">
              Buy Tickets
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              Experience Overview
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default FAQsPage;
